import { TabDataInterface, TileDataInterface } from "@me8eon/fav_storage";
import { TilesContainer, RenderTileFunction } from "react-tiles-dnd";
import { EonUiButton } from "@eon-ui/eon-ui-components-react";
import { useTranslation } from "@me8eon/translation";
import React, { useEffect, useState } from "react";
import "react-tiles-dnd/esm/index.css";
import "./TileComponent.scss";
import TileContent from "./TileContent";
import NewTileComponent from "./NewTileComponent";
import useSearchStore from "../../store/searchStore";
import { sortArray } from "../../util/common-util";
import useCurrentWidth from "../../util/useCurrentWidth";
import Modal from "../modal/Modal";
import AllAppsComponent from "../all-apps/AllAppsComponent";
import { useJwtToken, useUserData } from "@me8eon/react_authentication";
import { ROLE_ONBOARDING_PORTAL_USER, ROLE_ONBOARDING_PORTAL_REVIEWER } from '../../pages/onboarding-portal/OnboardingPortal.constants';
import { Icon } from "@me8eon/icons";

interface TileComponentInterface {
    isEditMode: boolean;
    favTabs: TabDataInterface[];
    favApps: TileDataInterface[];
    activeTab: string;
}
const TileComponent: React.FC<TileComponentInterface> = ({
    isEditMode,
    favTabs,
    favApps,
    activeTab
}) => {
    const { setPrefData, toggleFavourite, toggleEditMode, updateFavObject } =
        useSearchStore();
    const [madeChanges, setMadeChanges] = useState(false);
    const t = useTranslation();
    const jwt = useJwtToken();
    const { actualRoles } = useUserData();
    const [seqData, setSeqData] = useState<TileDataInterface[]>([]);
    const [currentTabFavApps, setCurrentTabFavApps] = useState<
        TileDataInterface[]
    >([]);
    const currentWidth = useCurrentWidth("main-content");
    const [tileWrapperWidth, setTileWrapperWidth] = useState("100%");
    const [tileWidth, setTileWidth] = useState(160);
    const [tileHeight, setTileHeight] = useState(180);
    const [allAppsModal, setAllAppsModal] = useState<boolean>(false);

    const applyWidth = (filteredTiles: TileDataInterface[]) => {
        // -68 for ignoring padding on screen
        const availableWidth = currentWidth - 100;
        const calculateWidth = Math.floor(availableWidth / 160) * 160;
        const widthAsData = filteredTiles.length * 160;
        if (currentWidth >= 440) {
            setTileWidth(160);
            setTileHeight(180);
            if (widthAsData <= calculateWidth) {
                const width = widthAsData > 800 ? 800 : widthAsData;
                setTileWrapperWidth(`${width + 1}px`);
            } else {
                const width = calculateWidth > 800 ? 800 : calculateWidth;
                setTileWrapperWidth(`${width + 1}px`);
            }
        } else {
            setTileWidth(240);
            setTileHeight(90);
            setTileWrapperWidth("100%");
        }
    };

    useEffect(() => {
        setMadeChanges(false);
        const filteredData = favApps.filter((obj) => obj.cat === activeTab);
        const data = sortArray(filteredData, "seq");
        setSeqData(data);
        applyWidth(data);
        setCurrentTabFavApps(filteredData);
    }, [favApps, favTabs, activeTab, isEditMode]);

    useEffect(() => {
        if (seqData.length > 0) {
            applyWidth(seqData);
        }
    }, [currentWidth]);

    const onReorderTiles = (reorderedData: TileDataInterface[]) => {
        const changed =
            JSON.stringify(currentTabFavApps) !== JSON.stringify(reorderedData);
        setMadeChanges(changed);
        setSeqData(reorderedData);
    };

    const handleDragSave = (action: string) => {
        if (action === "save") {
            const currentTabSortedApps = seqData.map((obj, index) => ({
                ...obj,
                seq: index + 1,
            }));
            const newSeqdata = favApps.map((obj) => {
                if (obj.cat === activeTab) {
                    const appInSorted = currentTabSortedApps.find(
                        (item) => item.alias === obj.alias
                    );
                    return appInSorted ?? obj;
                }
                return obj;
            });
            updateFavObject({ favApps: newSeqdata, favTabs });
            setPrefData(jwt);
        } else {
            setSeqData(favApps.filter((obj) => obj.cat === activeTab));
        }
        toggleEditMode(!isEditMode);
    };

    const handleDeleteTile = (tile: TileDataInterface) => {
        toggleFavourite(tile);
        setPrefData(jwt);
    };

    const tileData: RenderTileFunction<TileDataInterface> = ({
        data,
        isDragging,
    }) => {
        return (
            <div
                className={`card ${isEditMode ? "card--movable" : ""} ${isDragging ? "card--moving" : ""}`}
            >
                <TileContent
                    isEditMode={isEditMode}
                    data={data}
                    handleDeleteTile={handleDeleteTile}
                />
            </div>
        );
    };

    const toggleAllAppsModal = () => {
        setAllAppsModal(!allAppsModal);
    };

    return (
        <div className="tile-component">
            {favApps.length > 0 && <div
                style={{
                    width: tileWrapperWidth,
                }}
                className="drag-wrapper"
            >
                <div
                    className={`drag-container card-component ${isEditMode ? "highlight" : ""}`}
                >
                    {isEditMode ? (
                        <TilesContainer
                            data={seqData}
                            renderTile={tileData}
                            onReorderTiles={onReorderTiles}
                            forceTileWidth={tileWidth}
                            forceTileHeight={tileHeight}
                            disabled={false}
                        ></TilesContainer>
                    ) : (
                        <>
                            {
                                seqData.map((sdata, sDataIndex) => (
                                    <div
                                        className="card card-spaced"
                                        key={sDataIndex}
                                    >
                                        <TileContent
                                            isEditMode={false}
                                            data={sdata}
                                            handleDeleteTile={handleDeleteTile}
                                        />
                                    </div>
                                ))
                            }
                        </>
                    )}
                </div>
            </div>}
            {madeChanges && (
                <div className="common-button-wrapper">
                    <EonUiButton
                        text={t("save")}
                        rank="primary"
                        fullWidth={true}
                        size="small"
                        onClick={() => {
                            handleDragSave("save");
                        }}
                    ></EonUiButton>
                    <EonUiButton
                        text={t("cancel")}
                        rank="secondary"
                        fullWidth={true}
                        size="small"
                        onClick={() => {
                            handleDragSave("cancel");
                        }}
                    ></EonUiButton>
                </div>
            )}

            {currentTabFavApps.length === 0 && (
                <div style={{ textAlign: "center", marginTop: "40px" }}>
                    <span><Icon name="dashboard_large" themedColor="var(--primary-color)" /></span>
                    <div style={{ fontSize: "26px", margin: "18px", fontWeight: 500 }}>{t("allApps.addAppsTitle")}</div>
                    <div>{t("allApps.addAppsDesc")}</div>
                    <div style={{ marginTop: "12px" }}><b>{t("allApps.addAppsInfo")}</b></div>
                </div>
            )}

            <div>
                <div className="all-apps">
                    <EonUiButton
                        text={t("allApps.addAppsBtn")}
                        rank="primary"
                        size="small"
                        icon="add"
                        iconPosition="left"
                        onClick={() => {
                            toggleAllAppsModal();
                        }}
                    ></EonUiButton>
                    {((actualRoles.includes(ROLE_ONBOARDING_PORTAL_USER) || actualRoles.includes(ROLE_ONBOARDING_PORTAL_REVIEWER)) && (favTabs.length === 0 || activeTab === "Favourite")) && <EonUiButton
                        href="/onboarding-portal"
                        useAsLink
                        text={t("onboardingPortal.onboardAppBtn")}
                        rank="primary"
                        size="small"
                        icon="dashboard_02"
                        iconPosition="left"
                    />}
                </div>
                {currentTabFavApps.length > 0 && (<div style={{ textAlign: "center", marginBottom: "48px" }}>{t("allApps.addAppsDesc")}</div>)}
            </div>
            <Modal
                toggleModal={allAppsModal}
                closeModal={toggleAllAppsModal}
                isCloseIcon={true}
                closeOnBackdrop={false}
                modalClass="all-apps-modal"
            >
                <AllAppsComponent toggleAllAppsModal={toggleAllAppsModal} />
            </Modal>

        </div >
    );
};

export default TileComponent;
